import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, User, ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Styled Components
const ContactContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactForm = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      font-weight: 500;
      color: #374151;
      margin-bottom: 0.5rem;
    }
    
    input,
    textarea,
    select {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 1rem;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
    
    textarea {
      resize: vertical;
      min-height: 120px;
    }
  }
  
  .submit-btn {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border: none;
    border-radius: 0.5rem;
    padding: 0.875rem 2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  .info-card {
    background: white;
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    
    .info-icon {
      width: 3rem;
      height: 3rem;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      margin-bottom: 1rem;
    }
    
    h3 {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1a202c;
      margin-bottom: 0.75rem;
    }
    
    p {
      color: #64748b;
      line-height: 1.6;
      margin-bottom: 0.5rem;
    }
    
    .highlight {
      color: #3b82f6;
      font-weight: 600;
    }
  }
`;

const QuickLinks = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
  }
  
  .links-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }
  
  .link-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.5rem;
    color: #374151;
    text-decoration: none;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8fafc;
      color: #3b82f6;
      transform: translateX(4px);
    }
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    department: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simuler l'envoi du formulaire
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setShowSuccess(true);
    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      department: 'general'
    });
    
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const quickLinks = [
    { title: 'FAQ', icon: <MessageSquare size={16} />, href: '/faq' },
    { title: 'SAV', icon: <ChevronRight size={16} />, href: '/sav' },
    { title: 'Réparations', icon: <ChevronRight size={16} />, href: '/reparations' },
    { title: 'Retour produits', icon: <ChevronRight size={16} />, href: '/retours' }
  ];

  return (
    <ContactContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>Contactez-nous</h1>
              <p>Nous sommes là pour vous aider. Envoyez-nous un message et nous vous répondrons dans les plus brefs délais.</p>
            </PageHeader>
          </motion.div>

          <ContactGrid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ContactForm>
                <h2>Envoyer un message</h2>
                
                {showSuccess && (
                  <SuccessMessage>
                    <MessageSquare size={20} />
                    Votre message a été envoyé avec succès !
                  </SuccessMessage>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="department">Département</label>
                    <select
                      id="department"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="general">Support général</option>
                      <option value="sales">Ventes</option>
                      <option value="technical">Support technique</option>
                      <option value="reparations">Réparations</option>
                      <option value="sav">SAV</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="name">Nom complet</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="votre@email.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Téléphone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+225 XX XX XX XX"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subject">Sujet</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Sujet de votre message"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Décrivez votre demande en détail..."
                    />
                  </div>
                  
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    <Send size={18} />
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>
                </form>
              </ContactForm>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <ContactInfo>
                <div className="info-card">
                  <div className="info-icon">
                    <Phone size={24} />
                  </div>
                  <h3>Téléphone</h3>
                  <p className="highlight">+225 20 00 00 00</p>
                  <p>Lun - Ven: 8h - 18h</p>
                  <p>Sam: 9h - 15h</p>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <Mail size={24} />
                  </div>
                  <h3>Email</h3>
                  <p className="highlight">contact@inza-store.ci</p>
                  <p>support@inza-store.ci</p>
                  <p>Réponse sous 24h</p>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <MapPin size={24} />
                  </div>
                  <h3>Adresse</h3>
                  <p>Abidjan, Plateau</p>
                  <p>Face à la BCEAO</p>
                  <p>Immeuble Le Concorde, 2ème étage</p>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <Clock size={24} />
                  </div>
                  <h3>Horaires d'ouverture</h3>
                  <p>Lun - Ven: 8h - 18h</p>
                  <p>Sam: 9h - 15h</p>
                  <p>Dim: Fermé</p>
                </div>
              </ContactInfo>
            </motion.div>
          </ContactGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <QuickLinks>
              <h3>Liens rapides</h3>
              <div className="links-grid">
                {quickLinks.map((link, index) => (
                  <a 
                    key={index}
                    href={link.href}
                    className="link-item"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(link.href);
                    }}
                  >
                    {link.icon}
                    {link.title}
                  </a>
                ))}
              </div>
            </QuickLinks>
          </motion.div>
        </Container>
      </Section>
    </ContactContainer>
  );
};

export default ContactPage;
